import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeftIcon } from 'lucide-react';
import { GlowNumber } from '../components/GlowNumber';
import { MeaningCard, SectionCard } from '../components/MeaningCard';
import { AffirmationCard } from '../components/AffirmationCard';
import { NumberPill } from '../components/NumberPill';
import { SaveButton } from '../components/SaveButton';
import { getNumber } from '../data/numbers';
import { categoryMap, sectionOrder, typeMap } from '../data/categories';
import { useLibrary } from '../contexts/LibraryContext';
const ease = [0.23, 1, 0.32, 1] as const;
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function NumberDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const entry = getNumber(id);
  const {
    addRecentSearch
  } = useLibrary();
  useEffect(() => {
    if (entry) addRecentSearch(entry.id);
  }, [entry, addRecentSearch]);
  if (!entry) {
    return <div className="glass mt-10 rounded-4xl px-6 py-12 text-center" data-id="element-162" data-mp="84sfqlkwyofu">
        <p className="text-[17px] font-semibold text-white" data-id="element-163" data-mp="83hb6jjn9cno">That number isn’t in the library yet</p>
        <p className="mt-2 text-[13.5px] leading-relaxed text-lav-dim" data-id="element-164" data-mp="cwtp9zqp6126">
          Try another pattern — or browse the full collection.
        </p>
        <Link to="/explore" className="mt-6 inline-flex rounded-2xl border border-aura/45 bg-aura/[0.14] px-6 py-3.5 text-[14px] font-semibold text-white transition-[background-color,box-shadow] duration-200 ease-smooth hover:bg-aura/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-165" data-mp="lvnolxvex5jf">
          Explore numbers
        </Link>
      </div>;
  }
  const tags = entry.categories.map(category => categoryMap[category]).filter(Boolean).slice(0, 4);
  const alternateSpellings = Array.from(new Set(entry.searchAliases.filter(alias => alias !== entry.displayNumber && /^[\d:\-\s/]+$/.test(alias)))).slice(0, 3);
  const {
    datePattern,
    metadata
  } = entry;
  return <motion.article key={entry.id} initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.26,
    ease
  }} data-id="element-166" data-mp="2uk4s89hqaxf3">
      <button type="button" onClick={() => navigate(-1)} className="-ml-1 flex h-9 items-center gap-1.5 rounded-full pl-1 pr-3 text-[13px] font-medium text-lav-dim transition-colors duration-200 ease-smooth hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-167" data-mp="38i35fbkbcnt0">
        <ChevronLeftIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-168" data-mp="3s7wrra49eapu" />
        Back
      </button>

      <header className="mt-3 flex flex-col items-center text-center" data-id="element-169" data-mp="3n92680ewq821">
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold" data-id="element-170" data-mp="3n6z8svbfdcna">
          {typeMap[entry.type].label}
        </p>
        <div className="mt-6 mb-2" data-id="element-171" data-mp="erb8pidgsbyk">
          <GlowNumber value={entry.displayNumber} data-id="element-172" data-mp="eq9purq7il6a" />
        </div>
        <h1 className="mt-4 text-[24px] font-light leading-tight tracking-tight text-white" data-id="element-173" data-mp="rtiqzofmsnm">
          {entry.title}
        </h1>
        <p className="mt-3 max-w-[320px] text-[14px] leading-relaxed text-lav" data-id="element-174" data-mp="5ojytn08lo91">{entry.shortMeaning}</p>

        {datePattern && <p className="mt-3 text-[12px] text-lav-dim" data-id="element-175" data-mp="3n4s0903mect2">
            {monthNames[Math.min(Math.max(datePattern.month, 1), 12) - 1]} {datePattern.day}
            {datePattern.recurring ? ' · recurs every year' : ''}
          </p>}

        {alternateSpellings.length > 0 && <p className="mt-3 text-[11.5px] tabular-nums text-lav-dim" data-id="element-176" data-mp="3j2x224gzkfji">
            Also written as {alternateSpellings.join(' · ')}
          </p>}

        <div className="mt-5 flex flex-wrap justify-center gap-2" data-id="element-177" data-mp="395scrvrug3yh">
          {tags.map(tag => <span key={tag.id} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11.5px] font-medium text-lav-dim" data-id="element-178" data-mp="gbbnetvfrr2b">
              <span aria-hidden="true" className="mr-1" data-id="element-179" data-mp="6ls5bqfjs18i">
                {tag.icon}
              </span>
              {tag.label}
            </span>)}
        </div>

        <dl className="mt-6 grid w-full grid-cols-3 gap-2.5" data-id="element-180" data-mp="3stryhhdtp1k9">
          {[{
          label: 'Energy',
          value: metadata.energy
        }, {
          label: 'Theme',
          value: metadata.theme
        }, {
          label: 'Element',
          value: metadata.element
        }].map(item => <div key={item.label} className="glass rounded-2xl px-3 py-3" data-id="element-181" data-mp="3jv6ctdd216f9">
              <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-lav-dim" data-id="element-182" data-mp="3evjwuw5udc2r">
                {item.label}
              </dt>
              <dd className="mt-1 text-[13px] font-medium text-white" data-id="element-183" data-mp="1d2vqidxwi8ir">{item.value}</dd>
            </div>)}
        </dl>
      </header>

      <div className="mt-8 flex flex-col gap-3" data-id="element-184" data-mp="1kldhjy1tm5zh">
        {sectionOrder.map(({
        key,
        icon
      }) => <SectionCard key={key} icon={icon} section={entry[key]} accent={key === 'coreMeaning'} data-id="element-185" data-mp="1pjx1xu0rn9pq" />)}

        <MeaningCard icon="✨" title={`What to reflect on when you see ${entry.displayNumber}`} data-id="element-186" data-mp="1ucgfiqzge4uj">
          <ol className="flex flex-col gap-3" data-id="element-187" data-mp="1zahzde4pe872">
            {entry.reflectionPrompts.map((prompt, index) => <li key={prompt} className="flex gap-3" data-id="element-188" data-mp="11p3skfpdrv0p" data-mpi={index}>
                <span aria-hidden="true" className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-aura/30 text-[10.5px] font-semibold tabular-nums text-aura-bright" data-id="element-189" data-mp="16lkhvd8bgffd">
                  {index + 1}
                </span>
                <span className="text-[14px] leading-relaxed text-white/85" data-id="element-190" data-mp="2mzkfghjovszg">{prompt}</span>
              </li>)}
          </ol>
        </MeaningCard>
      </div>

      <div className="mt-3" data-id="element-191" data-mp="rx79uu9d5jwl">
        <AffirmationCard affirmation={entry.affirmation} data-id="element-192" data-mp="rwec2isdu4ql" />
      </div>

      <div className="mt-6" data-id="element-193" data-mp="mymfvckpfwgl">
        <SaveButton id={entry.id} label={entry.displayNumber} data-id="element-194" data-mp="mxb6mx8hpwi3" />
      </div>

      {entry.relatedNumbers.length > 0 && <section className="mt-9" data-id="element-195" data-mp="10xcfayuzy884">
          <h2 className="mb-3 text-[15px] font-semibold tracking-tight text-white" data-id="element-196" data-mp="wwk3bxuqpp85">
            Explore related numbers
          </h2>
          <div className="flex flex-wrap gap-2.5" data-id="element-197" data-mp="980vtkjilgh4">
            {entry.relatedNumbers.map(relatedId => {
          const related = getNumber(relatedId);
          if (!related) return null;
          return <NumberPill key={relatedId} id={related.id} label={related.displayNumber} subtle data-id="element-198" data-mp="i02ubda0wmor" />;
        })}
          </div>
        </section>}

      <p className="mt-8 text-[11.5px] leading-relaxed text-lav-dim/80" data-id="element-199" data-mp="3locu10m0yhmn">
        Interpretations are symbolic and reflective, drawn from spiritual traditions. They are not
        predictions, and not medical, financial or legal advice.
      </p>
    </motion.article>;
}